/**
 * Types for the Facilitator Portal — migrated from HTML prototypes.
 */

// ─── Facilitator ────────────────────────────────────────────────────────────

export type FacilitatorStatus = 'active' | 'pending' | 'inactive' | 'rejected';

export interface FacilitatorMeta {
  first_name?: string;
  family_name?: string;
  whatsapp?: string;
  city?: string;
  languages?: string[];
  bio?: string;
  preferred_participants?: string[];
  preferred_industries?: string[];
  preferred_audience?: string[];
}

export interface Facilitator {
  facilitator_id: string;
  display_name: string;
  email: string;
  status: FacilitatorStatus;
  timezone: string;
  max_sessions_per_day: number;
  max_sessions_per_week: number;
  qualified_workshops: string[];
  profile_data?: FacilitatorMeta | string;
  /** Parsed profile_data for convenience */
  _meta: FacilitatorMeta;
  created_at?: string;
  updated_at?: string;
}

// ─── Availability ───────────────────────────────────────────────────────────

/** Cell states for the Dashboard's 4-state availability grid */
export type AvailabilityCellState = 'specific' | 'recurring' | 'blocked' | null;

/** API slot shape returned by GET /facilitators/:id/availability */
export interface AvailabilitySlot {
  slot_type: 'recurring' | 'one_off' | 'blocked';
  start_time: string;
  end_time: string;
  effective_from?: string;
  day_of_week?: number;
  specific_date?: string;
}

export type AvailabilityViewMode = 'weekly' | 'monthly';

// ─── Proposals ──────────────────────────────────────────────────────────────

/** The server's statuses (demand_service.py, 2 Oct check). Only
 *  pending_facilitator asks this facilitator for an answer. */
export type ProposalStatus =
  | 'pending_admin'
  | 'pending_facilitator'
  | 'facilitator_confirmed'
  | 'published'
  | 'needs_admin_attention'
  | 'expired';

export interface Proposal {
  proposed_id: string;
  workshop_id: string;
  workshop_title: string;
  proposed_start: string;
  proposed_end: string;
  duration_minutes: number;
  matched_learner_count: number;
  max_participants?: number;
  facilitator_id: string | null;
  facilitator_deadline?: string | null;
  status: ProposalStatus;
  scheduled_session_id?: string | null;
  match_score?: number | null;
  match_metadata?: { recommended_facilitator?: { id?: string; fit_score?: number } | null } | null;
}

/** Proposals waiting for THIS facilitator's answer, deadline not passed. The
 *  facilitator check is a second lock: the server must also only send a
 *  facilitator their own (Scoring, 2 Oct). */
export function proposalsWaitingFor(list: Proposal[], facilitatorId: string, now = Date.now()): Proposal[] {
  return list.filter((p) => p.status === 'pending_facilitator'
    && p.facilitator_id === facilitatorId
    && (!p.facilitator_deadline || new Date(p.facilitator_deadline).getTime() > now));
}

// ─── Sessions ───────────────────────────────────────────────────────────────

export type SessionStatus =
  | 'draft'
  | 'published'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'facilitator_withdrawn';

export interface ScheduledSession {
  session_id: string;
  workshop_id: string;
  workshop_title: string;
  short_title?: string;
  facilitator_id: string;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  max_participants: number;
  confirmed_count: number;
  /** Learners who connected to the drill at least once (Scoring, 4 Oct: the
   *  same test as the refund rule). Absent until the server sends it. */
  attended_count?: number | null;
  status: SessionStatus;
  bridge_session_id?: string;
  /**
   * Whether the live drill has been launched, is running, or has ended.
   * Sent by the server on every scheduled session (Scoring 2026-09-28).
   * The slot KEEPS bridge_session_id after End (scoring still needs it),
   * so bridge_session_id alone cannot tell live from ended. Read this
   * through liveStatusOf(), never bridge_session_id directly.
   */
  live_status?: LiveStatus;
  /** Live Drills, 28 Oct: whether the session ran. Absent until the server
   *  sends it; read through playStateOf() (lib/playState). */
  play_status?: 'not_started' | 'live' | 'done' | 'not_played';
  /** Booked start + 15 min (ISO, UTC): the last moment it can still start. */
  start_window_ends_at?: string;
  /** On a not_played session only: false until every booked learner's
   *  refund is written, then true (Live Drills, 28 Oct). */
  refunds_complete?: boolean;
  created_by?: string;
  cancelled_by?: string;
  role_tier?: string;
  notes?: string;
  /** Live Coach venue model — Layer 2b, 2026-06-06.
   *  Present when the session has been pinned to a venue. NULL or
   *  absent = online drill. The list endpoint may not populate this;
   *  use the GET /api/live-coach/slots/{id} endpoint for the resolved
   *  venue + full venue details. */
  venue_id?: string | null;
}

/** Resolved venue subset returned by GET /api/live-coach/slots/{id}. */
export interface LiveCoachVenueRef {
  id: string;
  name: string;
  address?: string | null;
  region?: string | null;
  capacity?: number | null;
  notes?: string | null;
}

export interface LiveCoachSlotDetail {
  session_id: string;
  workshop_id: string;
  workshop_title: string;
  short_title?: string | null;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  facilitator_id?: string | null;
  status: string;
  venue: LiveCoachVenueRef | null;
  is_online: boolean;
}

export interface WorkshopContent {
  overview?: {
    title?: string;
    narrative?: string;
  };
  scenario_brief?: string;
  context_brief?: string;
  roles?: RoleTemplate[];
  twists?: Twist[];
  metadata?: {
    roleTier?: string;
  };
}

export interface RoleTemplate {
  letter: string;
  title: string;
  description: string;
  hidden_info: string;
}

export interface Twist {
  id: string;
  title: string;
  intensity: string;
  instructions: string;
}

// ─── Bridge (live session) ──────────────────────────────────────────────────

export type BridgePhase = 'setup' | 'live' | 'ended';
export type WSStatus = 'connected' | 'disconnected' | 'error';

export interface BridgeParticipant {
  participant_id: string;
  name: string;
  role: string;
  learner_id?: string;
  role_title?: string;
  role_description?: string;
  role_hidden_info?: string;
  connected: boolean;
  /** The server's own record of the card (Scoring, 3 Oct: 'pending' means
   *  "not on any device yet"). Used when this screen missed the live acks. */
  role_card_status?: 'pending' | 'sent' | 'received' | 'read';
  /** Where the learner is, from the server (Nino, 4 Oct; proposed to Scoring):
   *  join_page = drill open, not in the call; in_call; left = was in, isn't now. */
  presence?: 'join_page' | 'in_call' | 'left';
  /** Unix seconds. */
  left_at?: number | null;
  rejoined_at?: number | null;
  ever_in_call?: boolean;
}

export interface TwistFired {
  twist_id: string;
  title: string;
  timestamp: number;
  intensity?: string;
}

export interface BridgeSession {
  session_id: string;
  // The observer seat and the facilitator's 5-minute wait (Scoring, 7 Oct), on session_info.
  /** The viewer of this socket: on an observer's socket, the staff member themselves. */
  viewer?: { id: string; role: string; name: string } | null;
  facilitator?: { id: string; name: string } | null;
  facilitator_name?: string | null;
  /** False while the facilitator's socket is gone mid-drill. */
  facilitator_present?: boolean;
  /** ISO time the drill ends at if the facilitator isn't back (only while waiting). */
  facilitator_wait_until?: string | null;
  /** Why the server ended the drill, when it did (e.g. 'facilitator_did_not_return'). */
  end_reason?: string | null;
  workshop_id: string;
  workshop_title: string;
  join_code: string;
  phase: string;
  duration_minutes: number;
  role_tier?: string;
  participants: BridgeParticipant[];
  roles_released_at?: number;
  twists_fired: TwistFired[];
  available_twists: Twist[];
  /**
   * Epoch seconds when the bridge session STARTED / ENDED. Emitted by
   * the Bridge session_state message (src/api/bridge_ws.py on the
   * scoring-engine side) and read by useBridgeSession to render the
   * workshop timer. Added 2026-09-21 (AV's cluster-3 diagnostic) — the
   * fields have been on the wire since Jul 18 but weren't in the type,
   * which is what turned the FP typecheck red for two months.
   */
  started_at?: number;
  ended_at?: number;
  /**
   * Facilitator id, passed in on `launch()` and echoed back on session
   * state. Added 2026-09-25 for the LiveKit facilitator identity —
   * AV's correction memo confirmed the exclusion filter needs this
   * bare string (not a `facilitator_<id>` prefix) or a facilitator's
   * speech gets scored as a learner's. See facilitatorRoom.ts header
   * note 1.
   */
  facilitator_id?: string | null;
  /**
   * 'online' drills have a call; 'onsite' drills don't. Sent on
   * session_state. Used so the console can say "no call pass" loudly
   * for an online drill instead of hiding the call badge.
   */
  mode?: 'online' | 'onsite' | string;
  /** Reference to the scheduler session that spawned this */
  _scheduled?: ScheduledSession;
}

export interface RoleDeliveryEntry {
  participant_id: string;
  name: string;
  role: string;
  /** not_connected (Scoring, 3 Oct): owed, on no device yet; it goes out
   *  when the learner joins. */
  status: 'pending' | 'not_connected' | 'sent' | 'received' | 'read';
  /** Devices the card reached, on 'sent'. */
  devices?: number;
}

export interface RoleStatusPoll {
  participants: RoleDeliveryEntry[];
}

// ─── WebSocket message types ────────────────────────────────────────────────

/** All phases the Bridge session can be in */
export type BridgePhaseValue = 'setup' | 'act1' | 'twist' | 'act2' | 'debrief' | 'ended';

/** Server → Facilitator messages */
export type WSServerMessage =
  | { type: 'session_state'; data: Record<string, unknown> }
  | { type: 'pong'; timestamp: number }
  // already_released: the server's answer when roles were released before
  // (timestamp is then the first release). Asked of Scoring 2 Oct.
  | { type: 'roles_released'; participant_count: number; timestamp: number; already_released?: boolean }
  | { type: 'role_card_ack'; participant_id: string; name: string; status: string; devices?: number; timestamp: number }
  | ({ type: 'roles_state' } & RolesState)
  | { type: 'participant_presence'; participant_id: string; presence: 'join_page' | 'in_call' | 'left'; left_at?: number | null; rejoined_at?: number | null; ever_in_call?: boolean }
  | { type: 'assign_roles_refused'; reason: string; message?: string }
  | { type: 'twist_fired'; twist_id: string; title: string; timestamp: number }
  | { type: 'twist_blocked'; twist_id: string; reason: string }
  | { type: 'twist_ack'; participant_id: string; name: string; twist_id: string; timestamp: number }
  | { type: 'phase_changed'; phase: string }
  | { type: 'phase_change'; phase: string }
  | { type: 'timer_sync'; elapsed_seconds: number; remaining_seconds: number; phase: string }
  | { type: 'participant_connected'; participant_id: string; name: string; role: string }
  | { type: 'participant_disconnected'; participant_id: string; name: string }
  | { type: 'session_update'; data: Record<string, unknown> }
  | { type: 'participant_joined'; data: BridgeParticipant }
  | { type: 'participant_added'; session_id?: string; participant: { participant_id: string; name: string; role: string; connected?: boolean } }
  | { type: 'facilitator_presence'; present: boolean; wait_until?: string | null }
  // Session-wide (no participant_id) on phase changes, or per learner when
  // the call refuses to record them (Scoring, 1 Oct: state 'interrupted',
  // reason 'recording_limit' | 'recording_error'; 'recording' when a retry works).
  | { type: 'session_recording_state'; state: string; reason?: string | null; participant_id?: string; name?: string; ts?: number }
  // Drill chat and the observer seat (Scoring's format v1, 6 Oct).
  | ({ type: 'chat_message' } & Omit<import('@/lib/drillChat').ChatMessage, never>)
  | { type: 'observer_joined' | 'observer_left'; session_id?: string; observer: { id: string; name: string }; at?: string };

/** Facilitator → Server messages */
// ─── Role assignment (Nino, 3 Oct: the facilitator assigns roles) ───────────
// Proposed to Scoring 3 Oct ("role assignment, message proposal").

export interface DrillRole {
  letter: string;
  title: string;
  /** A core role must be filled for the drill to work (CA marks them). */
  core: boolean;
}

export interface RoleAssignment {
  participant_id: string;
  /** null = observer: no card, not scored. */
  role: string | null;
}

export interface RolesState {
  locked: boolean;
  roles: DrillRole[];
  /** Present learners only. */
  assignments: RoleAssignment[];
  core_empty: string[];
}

export type WSClientMessage =
  | { type: 'ping' }
  | { type: 'release_roles' }
  /** The facilitator's Quiet/Noisy choice for an in-person room (Live Drills 17 Oct; logged). Any other value clears it. */
  | { type: 'table_mode'; mode: 'quiet' | 'noisy' }
  | { type: 'assign_roles'; assignments: RoleAssignment[] }
  | { type: 'fire_twist'; twist_id: string; title: string; intensity: string; instructions: string }
  | { type: 'phase_change'; phase: string }
  | { type: 'timer_sync' };

// ─── Scoring metadata (from /api/bridge/session/:id/scoring-metadata) ──

export interface ScoringParticipant {
  participant_id: string;
  name: string;
  role: string;
  learner_id?: string;
  role_title: string;
  role_description: string;
  role_hidden_info?: string;
  role_card_status: 'pending' | 'sent' | 'received' | 'read';
  role_card_sent_at?: number;
  role_card_read_at?: number;
}

export interface ScoringTwist {
  twist_id: string;
  title: string;
  intensity: string;
  instructions: string;
  fired_at: number;
  ack_status?: Record<string, string>;
}

export interface PhaseTransition {
  timestamp: number;
  phase: string;
  type?: string;
}

export interface ActivityLogEntry {
  timestamp: number;
  type: string;
  detail: string;
}

export interface ScoringMetadata {
  session_id: string;
  workshop_id: string;
  workshop_title: string;
  facilitator_id: string;
  mode?: 'onsite' | 'online' | 'hybrid';
  role_tier?: string;
  duration_minutes: number;
  created_at?: number;
  started_at?: number;
  ended_at?: number;
  roles_released_at?: number;
  actual_duration_seconds?: number;
  scenario_text?: string;
  context_text?: string;
  participants: ScoringParticipant[];
  twists_fired: ScoringTwist[];
  phase_transitions: PhaseTransition[];
  activity_log: ActivityLogEntry[];
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00',
] as const;

export const AUDIENCE_TYPES = ['University Students', 'Professionals', 'Both'] as const;

/** Display labels for AUDIENCE_TYPES — Product asked Web (PB-204) to
 *  swap "Both" for "Open to all" wherever it surfaces in the FP
 *  scheduling form. Internal value (enum / DB string) stays the same
 *  to keep existing rows + API contracts intact; only the rendered
 *  label changes. Lane re-route 2026-05-06: this lives in Scoring's
 *  admin-dashboard since 2026-04-28. */
export const AUDIENCE_LABELS: Record<typeof AUDIENCE_TYPES[number], string> = {
  'University Students': 'University Students',
  'Professionals': 'Professionals',
  'Both': 'Open to all',
};

export const COMMON_TIMEZONES = [
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Asia/Dubai', 'Asia/Jakarta', 'Asia/Kuala_Lumpur', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
  'UTC',
] as const;

export const WA_COUNTRIES = [
  { code: '+62',  flag: '\u{1F1EE}\u{1F1E9}', name: 'Indonesia' },
  { code: '+60',  flag: '\u{1F1F2}\u{1F1FE}', name: 'Malaysia' },
  { code: '+65',  flag: '\u{1F1F8}\u{1F1EC}', name: 'Singapore' },
  { code: '+66',  flag: '\u{1F1F9}\u{1F1ED}', name: 'Thailand' },
  { code: '+63',  flag: '\u{1F1F5}\u{1F1ED}', name: 'Philippines' },
  { code: '+84',  flag: '\u{1F1FB}\u{1F1F3}', name: 'Vietnam' },
  { code: '+91',  flag: '\u{1F1EE}\u{1F1F3}', name: 'India' },
  { code: '+971', flag: '\u{1F1E6}\u{1F1EA}', name: 'UAE' },
  { code: '+966', flag: '\u{1F1F8}\u{1F1E6}', name: 'Saudi Arabia' },
  { code: '+44',  flag: '\u{1F1EC}\u{1F1E7}', name: 'UK' },
  { code: '+1',   flag: '\u{1F1FA}\u{1F1F8}', name: 'US' },
  { code: '+33',  flag: '\u{1F1EB}\u{1F1F7}', name: 'France' },
  { code: '+49',  flag: '\u{1F1E9}\u{1F1EA}', name: 'Germany' },
  { code: '+61',  flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia' },
  { code: '+81',  flag: '\u{1F1EF}\u{1F1F5}', name: 'Japan' },
  { code: '+82',  flag: '\u{1F1F0}\u{1F1F7}', name: 'South Korea' },
  { code: '+86',  flag: '\u{1F1E8}\u{1F1F3}', name: 'China' },
  { code: '+55',  flag: '\u{1F1E7}\u{1F1F7}', name: 'Brazil' },
  { code: '+234', flag: '\u{1F1F3}\u{1F1EC}', name: 'Nigeria' },
  { code: '+27',  flag: '\u{1F1FF}\u{1F1E6}', name: 'South Africa' },
] as const;

export const PARTICIPANT_OPTIONS = ['3-4', '4-5', '5-6'] as const;

export const INDUSTRIES = [
  'Healthcare & Pharmaceuticals',
  'Finance & Accounting',
  'Technology',
  'Retail & Consumer Goods',
  'Telecommunications',
  'Energy & Utilities',
  'Manufacturing',
  'Real Estate & Property',
  'Hospitality & Tourism',
  'Legal',
  'Education',
  'Consulting',
  'Media & Entertainment',
] as const;

// ─── Navigation ─────────────────────────────────────────────────────────────

export type FacilitatorPage =
  | 'dashboard'
  | 'sessions'
  | 'prep'
  // 'upload' removed 4 Oct: in-person upload is on the session panel (Design, Nino).
  // 'review' + 'local-results' removed 2026-06-12 (F6, Nino call):
  // moved to Command Centre — admin/dev surfaces, not facilitator workflows.
  | 'availability'
  | 'schedule'      // Wave 2.9 (2026-06-12): week-grid view
  | 'proposals'
  | 'active-drills' // Nino 2026-05-20: marketplace tab — pick + claim SDs
  | 'earnings'      // Wave 2.6 (2026-06-12): facilitator earnings ledger
  | 'wrapup'        // Redesign screen 3 (Nino, 1 Oct): after a drill ends
  | 'profile';

// ─── Live status ────────────────────────────────────────────────────────────

export type LiveStatus = 'not_launched' | 'live' | 'ended';

/**
 * Single source of truth for "is this drill live?" across the portal
 * (Sessions button, Dashboard hero + chips, sidebar live dot).
 *
 * Prefers the server's live_status. Falls back to inferring from
 * bridge_session_id ONLY when the field is absent (a server that
 * hasn't deployed Scoring's 2026-09-28 change yet) — that fallback is
 * the old, known-wrong-after-End behaviour, kept so the portal doesn't
 * lose its Launch button against an older server.
 */
export function liveStatusOf(s: { live_status?: LiveStatus; bridge_session_id?: string; status?: string }): LiveStatus {
  const raw: LiveStatus = s.live_status ?? (s.bridge_session_id ? 'live' : 'not_launched');
  // A session cancelled after launch is never shown as running (Platform, 19 Oct:
  // 757bc7e0 showed "LIVE" beside "Cancelled"). Cancelled wins.
  if (raw === 'live' && (s.status === 'cancelled' || s.status === 'facilitator_withdrawn')) return 'ended';
  return raw;
}
